import { isEmpty } from "lodash"

import { SECTION_TYPE } from "@/app/report/common/constants"
import { Axis } from "@/app/report/common/chart/scatter"
import {
  ChartRow,
  ChartScatterData,
  SectionScatterChart,
  SummaryImpl,
} from "@/app/report/common/constructor"
import { getDecimalPoint, parseSummary, parseUnit } from "@/app/report/common/tools"
import type { MetricMapType, ScatterChartSettings } from "@/app/report/common/type"
import type { PlayerReportInSessionSummary, ReportTemplate } from "@/types/report"
import type { Unit } from "@/types/setting"

/**
 * Scatter(선수별 두 지표 산점도) 섹션 — 리포트 화면(daily/page.tsx)과 PDF 빌더(BuilderScatterChart)가 함께 쓰는
 * 단일 구현 (일감 #75, 5단계-b). 축·점 데이터를 만드는 규칙은 이 파일 밖에 두지 않는다.
 */

/** 템플릿 Scatter 섹션 안의 차트 하나 (settings 배열 항목) */
export type TemplateScatterChart = {
  sectionIndex: number
  chartIndex: number
  settings: ScatterChartSettings
}

export const getTemplateScatterCharts = (
  template?: ReportTemplate | null,
): TemplateScatterChart[] =>
  (template?.content ?? []).flatMap((section, sectionIndex) =>
    section?.section === SECTION_TYPE.SCATTER_GRAPH && Array.isArray(section.settings)
      ? (section.settings as ScatterChartSettings[]).map((settings, chartIndex) => ({
          sectionIndex,
          chartIndex,
          settings,
        }))
      : [],
  )

export const resolveTemplateScatterChart = (
  charts: TemplateScatterChart[],
  sectionIndex?: number,
  chartIndex?: number,
): TemplateScatterChart | null =>
  charts.find(
    (chart) => chart.sectionIndex === sectionIndex && chart.chartIndex === (chartIndex ?? 0),
  ) ??
  charts[0] ??
  null

/** 차트 설정 하나 + 선수 요약 → ChartScatterData (점 하나 = 선수 하나, x·y = 두 지표 값) */
export const buildScatterChart = ({
  settings,
  players,
  metricMap,
  selectedUnitList,
  translateKey,
  decimalPlaces,
}: {
  settings: ScatterChartSettings
  players: PlayerReportInSessionSummary[] | undefined | null
  metricMap: MetricMapType
  selectedUnitList: Unit[]
  translateKey: (key: string) => string | undefined
  /** 소수점 자릿수 덮어쓰기 — 없으면 지표·단위 규칙 (PDF 빌더 아이템 옵션, 2026-10-01 요청) */
  decimalPlaces?: number
}): ChartScatterData => {
  const metricX = metricMap.get(settings.xAxis)
  const unitX = parseUnit(metricX, selectedUnitList, metricMap) ?? ""
  const decimalPointX = decimalPlaces ?? getDecimalPoint(metricX?.key, unitX)

  const metricY = metricMap.get(settings.yAxis)
  const unitY = parseUnit(metricY, selectedUnitList, metricMap) ?? ""
  const decimalPointY = decimalPlaces ?? getDecimalPoint(metricY?.key, unitY)

  const axisX = new Axis(
    translateKey(settings.xAxis) || settings.xAxis,
    decimalPointX,
    unitX,
    settings.xAxisManualScaleActive,
    settings.xAxisManualScaleValue,
  )
  const axisY = new Axis(
    translateKey(settings.yAxis) || settings.yAxis,
    decimalPointY,
    unitY,
    settings.yAxisManualScaleActive,
    settings.yAxisManualScaleValue,
  )

  const dataChart: Array<ChartRow> = []
  players?.forEach(({ summary, ...player }) => {
    const targetSummary = parseSummary(summary)

    let xVal = (targetSummary as SummaryImpl).get(settings.xAxis, metricX?.path[0] || "general")
    let yVal = (targetSummary as SummaryImpl).get(settings.yAxis, metricY?.path[0] || "general")
    if (xVal && typeof xVal === "object" && "value" in xVal) {
      xVal = (xVal as { value: number }).value
    }
    if (yVal && typeof yVal === "object" && "value" in yVal) {
      yVal = (yVal as { value: number }).value
    }

    const info = player as Record<string, unknown>
    const row = new ChartRow()
    row.name = `${info["backNumber"]}`
    row.header.set("title", `${info["backNumber"]} ${info["playerName"]}`)
    row.data.push(Number(xVal))
    row.data.push(Number(yVal))
    dataChart.push(row)
  })

  return new ChartScatterData({ settings, axisX, axisY, data: dataChart })
}

/** 템플릿 Scatter 섹션(차트 여러 개) → SectionScatterChart. 설정이 없으면 null */
export const buildScatterSection = ({
  scatterChartSettings,
  players,
  metricMap,
  selectedUnitList,
  translateKey,
}: {
  scatterChartSettings: ScatterChartSettings[] | undefined | null
  players: PlayerReportInSessionSummary[] | undefined | null
  metricMap: MetricMapType
  selectedUnitList: Unit[]
  translateKey: (key: string) => string | undefined
}): SectionScatterChart | null => {
  if (!scatterChartSettings || isEmpty(scatterChartSettings)) {
    return null
  }
  const settings = structuredClone(scatterChartSettings)

  return new SectionScatterChart(
    settings.map((settingsItem) =>
      buildScatterChart({
        settings: settingsItem,
        players,
        metricMap,
        selectedUnitList,
        translateKey,
      }),
    ),
  )
}
